import { MoreIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom-v5-compat';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import WorkflowStatusSticker from 'dashboard/components/WorkflowStatusSticker';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import TableLayout, {
  TableActionTD,
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { AccountAction } from 'dashboard/models/account-action';
import { WorkflowStatus } from 'dashboard/models/workflow';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { Paragraph } from 'design-system/components/Text';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';

interface AccountActionsTableProps {
  isLoading: boolean;
  sortParams: SortParams;
  hasUsedFilters: boolean;
  accountTypeId: string;
  accountActions: AccountAction[];
  handleSortUpdate: (sortParams: SortParams) => void;
  handleArchiveAccountAction: (accountAction: AccountAction) => void;
  handleActivateAccountAction: (accountAction: AccountAction) => void;
  handleDeactivateAccountAction: (accountAction: AccountAction) => void;
}

const AccountActionsTable = ({
  isLoading,
  sortParams,
  accountTypeId,
  accountActions,
  hasUsedFilters,
  handleSortUpdate,
  handleArchiveAccountAction,
  handleActivateAccountAction,
  handleDeactivateAccountAction,
}: AccountActionsTableProps) => {
  const [
    showArchiveAccountActionModal,
    { prompt: archivePrompt, confirm: confirmArchive, cancel: cancelArchive },
  ] = useConfirmation();
  const [
    showActivateAccountActionModal,
    { prompt: activatePrompt, confirm: confirmActivate, cancel: cancelActivate },
  ] = useConfirmation();
  const [
    showDeactivateAccountActionModal,
    { prompt: deactivatePrompt, confirm: confirmDeactivate, cancel: cancelDeactivate },
  ] = useConfirmation();
  const hasAccountActionWrite = usePermission(Permission.AccountActionWrite);

  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const actionRows = accountActions.map((action) => {
    const createdAt = DateTime.fromISO(action.attributes['created-at']).toLocaleString(
      DateTime.DATETIME_MED,
    );
    const lastEditedAt = action.attributes['updated-at']
      ? DateTime.fromISO(action.attributes['updated-at']).toLocaleString(DateTime.DATETIME_MED)
      : '–';

    const handleClickArchiveAccountAction = () =>
      archivePrompt(() => handleArchiveAccountAction(action));
    const handleClickActivateAccountAction = () =>
      activatePrompt(() => handleActivateAccountAction(action));
    const handleClickDeactivateAccountAction = () =>
      deactivatePrompt(() => handleDeactivateAccountAction(action));

    const hasPublishedVersion = !!action.relationships['latest-published-version']?.data?.id;

    const moreItems = [
      {
        id: `action-menu__runs--${action.id}`,
        label: 'Runs',
        routeTo: `/dashboard/account-types/${accountTypeId}/actions/${action.id}/runs`,
      },
      {
        id: `action-menu__archive--${action.id}`,
        label: 'Archive',
        variant: 'danger' as const,
        onClick: handleClickArchiveAccountAction,
        disabled: !hasAccountActionWrite,
        hidden: action.attributes.status === WorkflowStatus.Archived,
      },
      {
        id: `action-menu__activate--${action.id}`,
        label: 'Activate',
        onClick: handleClickActivateAccountAction,
        disabled: !hasAccountActionWrite,
        hidden: !hasPublishedVersion || action.attributes.status !== WorkflowStatus.Paused,
      },
      {
        id: `action-menu__deactivate--${action.id}`,
        label: 'Deactivate',
        onClick: handleClickDeactivateAccountAction,
        disabled: !hasAccountActionWrite,
        hidden: !hasPublishedVersion || action.attributes.status !== WorkflowStatus.Running,
      },
    ].filter((item) => !item.hidden);

    const actionWorkflowVersionId =
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      action.relationships['latest-draft-version'].data?.id ??
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      action.relationships['latest-published-version'].data?.id;

    return (
      <TableLinkTR
        id={`account-type-account-actions-table__action-row-${action.id}`}
        key={action.id}
        to={`/dashboard/account-types/${accountTypeId}/actions/${action.id}/${actionWorkflowVersionId}`}
      >
        <TableTD>{action.attributes.name}</TableTD>
        <TableTD>
          <WorkflowStatusSticker workflowStatus={action.attributes.status} />
        </TableTD>
        <TableTD>{createdAt}</TableTD>
        <TableTD>{lastEditedAt}</TableTD>
        <TableActionTD>
          <DropdownMenu
            trigger={
              <Button
                aria-label={`More actions for ${action.id}`}
                id={`account-actions-table-row__action-menu--${action.id}`}
                variant='inverse'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                width='icon'
              >
                <MoreIcon />
              </Button>
            }
          >
            {moreItems.map((item) =>
              item.routeTo ? (
                <DropdownMenu.Item as={Link} id={item.id} key={item.id} to={item.routeTo}>
                  {item.label}
                </DropdownMenu.Item>
              ) : (
                <DropdownMenu.Item
                  id={item.id}
                  key={item.id}
                  variant={item.variant ?? 'basic'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    item.onClick();
                  }}
                  disabled={item.disabled}
                >
                  {item.label}
                </DropdownMenu.Item>
              ),
            )}
          </DropdownMenu>
        </TableActionTD>
      </TableLinkTR>
    );
  });

  return (
    <>
      <TableLayout
        headerTR={
          <>
            <TableTH>Name</TableTH>
            <TableTH>Status</TableTH>
            <TableTH>{`Created at (${DateTime.local().toFormat(
              TIMEZONE_NAME_ABBREVIATED,
            )})`}</TableTH>
            <TableTH
              id='account-actions-table__updated-at-header'
              isSorted={sortParams.sort === 'updated_at'}
              canSort={true}
              isDescending={sortParams.shouldSortDescending}
              onClick={() => handleColumnHeaderClick('updated_at')}
            >{`Last edited at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}</TableTH>
            <TableTH>{/* Actions column */}</TableTH>
          </>
        }
        isLoading={isLoading}
        dataTest='account-actions-table'
        isEmpty={accountActions.length === 0}
        nullState={
          <TableNullState
            hasUsedFilters={hasUsedFilters}
            dataTestBlock='account-type-account-action-table__null-state'
          >
            <Paragraph>
              Looks like there aren&#39;t any actions configured for this account type yet.
            </Paragraph>
          </TableNullState>
        }
      >
        {actionRows}
      </TableLayout>
      <ModalWrapper isOpen={showArchiveAccountActionModal} handleCloseModal={cancelArchive}>
        <ConfirmationModal
          cancelAction={cancelArchive}
          confirmAction={confirmArchive}
          header='Archive this account action?'
          confirmActionText='Archive'
        >
          <p>
            This action is permanent and can’t be undone. You will no longer be able to configure
            this account action.
          </p>
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showActivateAccountActionModal} handleCloseModal={cancelActivate}>
        <ConfirmationModal
          cancelAction={cancelActivate}
          confirmAction={confirmActivate}
          header='Activate this account action?'
          confirmActionText='Activate'
          confirmButtonType='primary'
        >
          This account action will be activated across all environments.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showDeactivateAccountActionModal} handleCloseModal={cancelDeactivate}>
        <ConfirmationModal
          cancelAction={cancelDeactivate}
          confirmAction={confirmDeactivate}
          header='Deactivate this account action?'
          confirmActionText='Deactivate'
          confirmButtonType='primary'
        >
          This account action will be deactivated across all environments.
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

export default AccountActionsTable;
